import { findIndex, throttle } from 'lodash-es';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AnchorLine } from './';
import { getMarkerEndpoints } from '../../../utils/adsMarker';
import FormatUtils from '../../../utils/FormatUtils';
import { NotificationOutlined } from '@ant-design/icons';
import PositionBadge from '../PositionBadge';

const MARKER_WIDTH = 84;

const AdMarker = ({
  markers,
  marker,
  episodeDuration,
  silenceDetected = [],
  pixelsPerSecond,
  handleMarkerChange,
  setSelectedMarkerId,
  selectedMarkerId,
}) => {
  const [minStart, maxStart] = getMarkerEndpoints(markers, marker.placement, episodeDuration);
  const [startSecond, setStartSecond] = useState(Math.min(marker.start, episodeDuration));
  const [markerOffset, setMarkerOffset] = useState(
    (startSecond / (episodeDuration || 1)) * MARKER_WIDTH
  );
  const [mouseAway, setMouseAway] = useState(0);
  const [mouseOffsetLeftInMarker, setMouseOffsetLeftInMarker] = useState(0);
  const [activeSilence, setActiveSilence] = useState(false);
  const selected = marker.placement === selectedMarkerId;

  const markerRef = useRef();

  useEffect(() => {
    setStartSecond(Math.min(marker.start, episodeDuration));
    setMarkerOffset(
      (Math.min(marker.start, episodeDuration) / (episodeDuration || 1)) * MARKER_WIDTH
    );
  }, [marker.start, episodeDuration]);

  const getCurrentPosition = (event) => {
    const newMarkerOffset = calculateMarkerOffsetFromMousePosition(event.clientX);
    let newStartSecond = calculateStart(event.clientX, markerOffset);
    const starts = silenceDetected.map((s) => s.end - s.duration);
    const index = findIndex(starts, (s) => newStartSecond > s && newStartSecond < s);
    if (index > -1 && starts[index] > 0) {
      newStartSecond = starts[index];
    }
    return { newStartSecond, newMarkerOffset, sticked: index > -1 };
  };

  const getMouseAway = (event) => {
    const markerPosition = markerRef.current.getBoundingClientRect().x;
    const mousePosition = event.clientX;
    return mousePosition - markerPosition;
  };

  const calculateMarkerOffsetFromMousePosition = (mouseOffsetLeft) => {
    const { width: timelineWidth = 1, left: timelineOffsetLeft = 0 } =
      markerRef.current.offsetParent.getBoundingClientRect();
    const mouseOffsetLeftInTimeline = mouseOffsetLeft - Math.round(timelineOffsetLeft);
    const offset = (mouseOffsetLeftInTimeline / timelineWidth) * MARKER_WIDTH;

    return Math.min(MARKER_WIDTH, Math.max(0, offset));
  };

  const calculateStart = (mouseOffsetLeft, markerOffset) => {
    const { left: timelineOffsetLeft } = markerRef.current.offsetParent.getBoundingClientRect();
    const mouseOffsetLeftInTimeline = mouseOffsetLeft - Math.round(timelineOffsetLeft);
    const offset = mouseOffsetLeftInTimeline - mouseOffsetLeftInMarker + markerOffset;
    const clampSecondsToEpisode = Math.min(maxStart, Math.max(minStart, offset / pixelsPerSecond));
    return clampSecondsToEpisode;
  };

  const handleMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { left: timelineOffsetLeft = 0 } = markerRef.current.getBoundingClientRect();
    const mouseOffsetLeftInMarker = event.clientX - Math.round(timelineOffsetLeft);

    setMouseOffsetLeftInMarker(mouseOffsetLeftInMarker);
    setSelectedMarkerId(marker.placement);
  };

  const handleMouseMove = throttle((event) => {
    if (selected) {
      const { newStartSecond, newMarkerOffset, sticked } = getCurrentPosition(event);
      const currentMouseAway = getMouseAway(event);
      setMouseAway(currentMouseAway);
      setActiveSilence(sticked);
      setStartSecond(newStartSecond);
      setMarkerOffset(newMarkerOffset);
    }
  }, 50);

  const handleMouseUp = (event) => {
    if (selected) {
      const { newStartSecond } = getCurrentPosition(event);
      handleMarkerChange(marker.placement, newStartSecond);
      setMouseOffsetLeftInMarker(0);
      setMouseAway(0);
      setSelectedMarkerId(null);
    }
  };

  return (
    <div className="ignoreSeekClick">
      <AnchorLine
        offsetLeft={startSecond * pixelsPerSecond}
        type={marker.placement}
        active={activeSilence}
        isMax={startSecond >= maxStart}
        isMin={startSecond <= minStart}
        mouseAway={mouseAway}
      />
      <div
        ref={markerRef}
        style={{
          width: MARKER_WIDTH,
          transform: `translateX(${startSecond * pixelsPerSecond - markerOffset}px)`,
        }}
        className={`marker marker--ad ${selected ? 'marker--selected' : ''} `}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <PositionBadge
          selected={selected}
          active={activeSilence}
          icon={<NotificationOutlined />}
          type={marker.placement}
          label={FormatUtils.formatTimeHundredthSeconds(startSecond)}
        />
      </div>
    </div>
  );
};

export default AdMarker;

AdMarker.propTypes = {
  marker: PropTypes.object.isRequired,
  markers: PropTypes.array.isRequired,
  silenceDetected: PropTypes.array,
  pixelsPerSecond: PropTypes.number.isRequired,
  handleMarkerChange: PropTypes.func.isRequired,
  episodeDuration: PropTypes.number,
  setSelectedMarkerId: PropTypes.func.isRequired,
  selectedMarkerId: PropTypes.string,
};
